import { Link, Navigate } from "react-router-dom";
import { ArrowRight, Calculator, Bank, Fire, Toolbox, Truck, Broadcast } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { useSeo } from "@/lib/seo";
import { SOLUTIONS, SOLUTION_ORDER } from "@/lib/marketing";
import { PublicShell } from "@/components/PublicShell";
import { VideoStrip } from "@/components/VideoStrip";

const TOOLS = [
  { to: "/tools/freight-calculator", Icon: Calculator, title: "Freight quote calculator",
    body: "Compare every carrier on your lane on landed cost — base rate, fuel, customs and transit days. No sign-in." },
  { to: "/tools/finance-checker", Icon: Bank, title: "Finance & scheme checker",
    body: "See the instalment, the true cost of credit and which government schemes you qualify for. No sign-in." },
];

const PICKS = [
  { to: "/solutions/inventory", Icon: Toolbox, key: "inventory" },
  { to: "/solutions/freight", Icon: Truck, key: "freight" },
  { to: "/solutions/safety", Icon: Fire, key: "safety" },
];

export default function Home() {
  const { user, loading } = useAuth();
  useSeo({
    title: "Unity & Utility — inventory, tool crib, freight, safety and finance in one platform",
    description: "One platform for industrial floor operations: stock and tool control, freight quote comparison, fleet dispatch, fire and flood safety records, insurance, lender and government scheme comparison, hiring and an AI assistant.",
    path: "/",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      name: "Unity & Utility",
      applicationCategory: "BusinessApplication",
      operatingSystem: "Web",
      description: "Industrial floor operations, supply, logistics, safety and finance in one web platform.",
      offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
    },
  });

  if (loading) return null;
  if (user) return <Navigate to="/dashboard" replace />;

  return (
    <PublicShell>
      <section className="mx-auto max-w-5xl px-5 pb-4 pt-14 sm:px-8 sm:pt-20">
        <p className="overline text-[#002FA7]">Industrial floor operations</p>
        <h1 data-testid="home-h1" className="font-display mt-3 max-w-3xl text-4xl font-bold leading-[1.05] tracking-tight text-[#0F172A] sm:text-5xl lg:text-6xl">
          Run the floor, the road and the paperwork from one place
        </h1>
        <p className="mt-5 max-w-2xl text-base leading-relaxed text-slate-600">
          Stock and tools under control. Freight compared before you book. Fire and flood records that
          stand up to an inspection. Lenders, insurers and government schemes compared on the real
          numbers. Built for workshops, warehouses, contractors and local carriers.
        </p>
        <div className="mt-8 flex flex-wrap items-center gap-3">
          <Link to="/register" data-testid="home-cta-register" className="inline-flex items-center gap-2 bg-[#002FA7] px-5 py-3 text-sm font-semibold text-white transition-colors duration-150 hover:bg-[#00248A]">
            Get started free <ArrowRight size={16} />
          </Link>
          <Link to="/tools/freight-calculator" data-testid="home-cta-freight" className="inline-flex items-center gap-2 border border-[#0F172A] px-5 py-3 text-sm font-semibold text-[#0F172A] transition-colors duration-150 hover:border-[#002FA7] hover:text-[#002FA7]">
            Compare freight quotes
          </Link>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-5 py-12 sm:px-8">
        <h2 className="text-base font-semibold text-[#0F172A] md:text-lg">Free to use, no account needed</h2>
        <div className="mt-4 grid gap-3 sm:grid-cols-2" data-testid="home-tools">
          {TOOLS.map(({ to, Icon, title, body }) => (
            <Link key={to} to={to} data-testid={`home-tool-${to.split("/").pop()}`} className="group border border-border bg-white p-5 transition-shadow duration-200 hover:shadow-[0_2px_18px_rgba(15,23,42,0.07)]">
              <Icon size={22} className="text-[#002FA7]" />
              <div className="mt-3 text-sm font-semibold text-[#0F172A]">{title}</div>
              <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{body}</p>
              <span className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-[#002FA7]">
                Open it <ArrowRight size={13} className="transition-transform duration-150 group-hover:translate-x-0.5" />
              </span>
            </Link>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-5 py-4 sm:px-8">
        <h2 className="text-base font-semibold text-[#0F172A] md:text-lg">Where it earns its keep</h2>
        <div className="mt-4 grid gap-3 sm:grid-cols-3" data-testid="home-picks">
          {PICKS.map(({ to, Icon, key }) => (
            <Link key={to} to={to} data-testid={`home-pick-${key}`} className="border border-border bg-white p-5 transition-colors duration-200 hover:border-[#002FA7]">
              <Icon size={20} className="text-[#002FA7]" />
              <div className="mt-3 text-sm font-semibold text-[#0F172A]">{SOLUTIONS[key].h1}</div>
              <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{SOLUTIONS[key].lead}</p>
            </Link>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-5 py-12 sm:px-8">
        <h2 className="text-base font-semibold text-[#0F172A] md:text-lg">Everything in the platform</h2>
        <ul className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-3" data-testid="home-solution-links">
          {SOLUTION_ORDER.map((slug) => (
            <li key={slug}>
              <Link to={`/solutions/${slug}`} data-testid={`home-solution-${slug}`} className="block border border-border bg-white px-4 py-3 text-sm text-slate-700 transition-colors duration-150 hover:border-[#002FA7] hover:text-[#002FA7]">
                {SOLUTIONS[slug].h1}
              </Link>
            </li>
          ))}
        </ul>
      </section>

      <VideoStrip limit={3} heading="Latest from the channel" />

      <section className="mx-auto max-w-5xl px-5 pb-16 sm:px-8">        <div className="border border-border bg-white p-6 sm:p-8">
          <Broadcast size={20} className="text-[#002FA7]" />
          <h2 className="mt-3 text-base font-semibold text-[#0F172A] md:text-lg">See it live</h2>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-slate-600">
            Public posts from the floor and the road, open vacancies and surplus stock for sale — all
            on one public page you can share to WhatsApp, Facebook, X or LinkedIn in one tap.
          </p>
          <Link to="/feed" data-testid="home-cta-feed" className="tap-safe mt-4 inline-flex items-center gap-2 text-sm font-semibold text-[#002FA7] hover:underline">
            Open the live feed <ArrowRight size={14} />
          </Link>
        </div>
      </section>
    </PublicShell>
  );
}
